<template>
  <div class="content-details">
    <p-reading-progress />
    <div class="content-details__container">
      <div class="content-details__wrapper">
        <div
          class="content-details__content"
          :class="{ 'content-details__content--loading': loading }"
        >
          <template v-if="loading">
            <DetailSkeleton />
          </template>

          <template v-else-if="content">
            <p-breadcrumb
              :items="[
                { label: $t('general.home'), to: '/' },
                ...(content?.categories || []).map((category, index) => {
                  let link = category.slug
                  if (0 < index && content?.categories?.[0]) {
                    link = `${content.categories[0].slug}/${category.slug}`
                  }
                  return {
                    label: category.title,
                    to: appLink(link),
                  }
                }),
                { label: $t('general.article'), to: '' },
              ]"
            />

            <p-content-title
              :title="content.title"
              :date="content.published_at"
              :reading-time="content.read_duration_minutes"
            />
            <div
              class="content-details__tabs"
              :class="{ 'content-details__tabs--single': visibleTabs.length <= 1 }"
            >
              <p-tab v-model="selectedTab">
                <p-tab-panel
                  v-if="content?.content?.web"
                  id="web"
                  :title="$t('general.deviceTabs.web')"
                >
                  <div
                    class="content-details__body"
                    @click="onArticleClick"
                    v-html="transformHtml(content.content.web)"
                  />
                  <div class="content-details__author">
                    <p class="f-utility-14 t-fg-secondary">{{ $t('general.author') }}:</p>
                    <p class="f-utility-14-medium t-fg-primary">Paribu</p>
                  </div>
                </p-tab-panel>
                <p-tab-panel
                  v-if="content?.content?.mobile"
                  id="mobile"
                  :title="$t('general.deviceTabs.mobile')"
                >
                  <div
                    class="content-details__body"
                    @click="onArticleClick"
                    v-html="transformHtml(content.content.mobile)"
                  />
                  <div class="content-details__author">
                    <p class="f-utility-14 t-fg-secondary">{{ $t('general.author') }}:</p>
                    <p class="f-utility-14-medium t-fg-primary">Paribu</p>
                  </div>
                </p-tab-panel>
              </p-tab>

              <p-content-sidebar
                v-if="!loading && content?.content && headings.length > 0"
                :content="content.content[selectedTab]"
                :title="content.title"
              />
            </div>

            <p-dynamic-form
              v-if="showDynamicForm && extendedFormSchema"
              :schema="extendedFormSchema as any"
              class="p-dynamic-form"
              @submit="handleDynamicFormSubmit"
            />
            <div v-if="formSubmitted" class="py-32 text-center">
              <p-icon icon="check" size="xxl" class="justify-content-center" />
              <h2 class="f-title-32-bold mt-32">{{ $t('form.submission.title') }}</h2>
              <p class="f-utility-16 mt-8 t-fg-secondary">
                {{ $t('form.submission.description') }}
              </p>
            </div>

            <!-- <div class="announcement-details__feedback">
              <p-divider />
              <p-help-feedback @on-feedback="handleFeedback" @on-submit="handleSubmit" />
              <p-divider />
            </div> -->

            <div
              v-if="content?.similar && content.similar.length > 0"
              class="content-details__related-articles"
            >
              <h2 class="f-title-20-bold t-fg-primary mb-16">
                {{ $t('general.relatedArticles') }}
              </h2>
              <div class="content-details__related-articles-list">
                <p-article-link
                  v-for="(article, index) in content.similar"
                  :key="`related-article-${index}`"
                  size="md"
                  :label="article.title"
                  :to="article.url"
                  class="content-details__related-articles-item"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <p-content-float-button
      v-if="$device.isMobile && !loading && content?.content && headings.length > 0"
      @on-click="showMobileDrawer = true"
    />

    <p-drawer
      v-if="$device.isMobile && showMobileDrawer"
      :is-show-overlay="true"
      :steps="{ content: { title: 'İçerikler', url: 'content' } }"
      @on-close="showMobileDrawer = false"
    >
      <template #content>
        <div class="content-mobile-navigation">
          <nav class="content-mobile-navigation__nav">
            <ul class="content-mobile-navigation__list">
              <li v-if="content?.title" class="content-mobile-navigation__item">
                <a
                  href="#"
                  class="content-mobile-navigation__link f-utility-14 t-fg-secondary"
                  :class="{
                    'content-mobile-navigation__link--active': activeHeading === TITLE_ID,
                  }"
                  @click.prevent="handleMobileTitleClick"
                >
                  {{ content.title }}
                </a>
              </li>

              <li
                v-for="(heading, index) in headings"
                :key="index"
                class="content-mobile-navigation__item"
              >
                <a
                  :href="`#${heading.id}`"
                  class="content-mobile-navigation__link f-utility-14 t-fg-secondary"
                  :class="{
                    'content-mobile-navigation__link--active': activeHeading === heading.id,
                  }"
                  @click.prevent="handleMobileHeadingClick(heading.id)"
                >
                  {{ heading.text }}
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </template>
    </p-drawer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'

import { useUserStore } from '@/store/user'
import { useAppLink } from '@/composables/useAppLink'
import { useArticleHtml } from '@/composables/useArticleHtml'
import PDynamicForm from '@/components/pages/Content/PDynamicForm/PDynamicForm.vue'
import PContentSidebar from '@/components/ui/PContentSidebar/PContentSidebar.vue'
import PContentFloatButton from '@/components/ui/PContentFloatButton/PContentFloatButton.vue'
import DetailSkeleton from '@/components/shared/DetailSkeleton.vue'
// import { useVoteById } from '@/api/useContent'
import { useFormSubmit } from '@/api/useForm'
import { type Similar, type Seo } from '@/types/content'

interface FormField {
  [key: string]: any
}

interface Form {
  name: string
  is_login_required: boolean
  fields: FormField[]
}

interface ContentCategory {
  id: string
  title: string
  slug: string
}

interface Content {
  id: string
  title: string
  published_at: string
  read_duration_minutes: number
  content: {
    mobile: string
    web: string
  }
  seo?: Seo
  similar?: Similar[]
  form?: Form
  categories: ContentCategory[]
}

interface Heading {
  id: string
  text: string
}

const props = defineProps<{
  content: Content | null
  loading: boolean
}>()

const { appLink } = useAppLink()
const { transformHtml, onArticleClick } = useArticleHtml()
const route = useRoute()
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const isInitializing = computed(() => userStore.isInitializing)
// const { fetchVoteById } = useVoteById()
const { submitForm } = useFormSubmit()

// SEO meta tags
useHead({
  title: computed(() => props.content?.seo?.meta_title || props.content?.title || ''),
  meta: computed(() => {
    const meta: Array<{ name: string; content: string; key?: string }> = []

    const description = props.content?.seo?.meta_description
    if (description) {
      meta.push({ name: 'description', content: description, key: 'description' })
    }

    const robotsContent =
      props.content?.seo?.index === false ? 'noindex, nofollow' : 'index, follow'
    meta.push({ name: 'robots', content: robotsContent, key: 'robots' })

    return meta
  }),
})

// JSON-LD: Article + BreadcrumbList (SEO structured data)
const requestUrl = useRequestURL()
const pageUrl = computed(() => `${requestUrl.origin}${route.path}`)

const articleJsonLd = computed(() => {
  if (!props.content) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: props.content.title,
    datePublished: props.content.published_at || undefined,
    dateModified: props.content.published_at || undefined,
    inLanguage: route.path.startsWith('/en') ? 'en' : 'tr',
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl.value },
    author: { '@type': 'Organization', name: 'Paribu' },
    publisher: {
      '@type': 'Organization',
      name: 'Paribu',
      logo: {
        '@type': 'ImageObject',
        url: `${requestUrl.origin}/destek/images/paribu-support-logo.svg`,
      },
    },
  }
})

const breadcrumbJsonLd = computed(() => {
  const items: Array<{
    '@type': 'ListItem'
    position: number
    name: string
    item: string
  }> = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Anasayfa',
      item: `${requestUrl.origin}/destek/`,
    },
  ]

  const cats = props.content?.categories || []
  cats.forEach((category, index) => {
    let link = category.slug
    if (index > 0 && cats[0]) link = `${cats[0].slug}/${category.slug}`
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: category.title,
      item: `${requestUrl.origin}${appLink(link)}`,
    })
  })

  if (props.content?.title) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: props.content.title,
      item: pageUrl.value,
    })
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  }
})

const safeJsonLd = (obj: unknown) => JSON.stringify(obj).replace(/</g, '\\u003c')

useHead({
  script: computed(() => {
    const scripts: Array<{ type: string; innerHTML: string; key: string }> = []
    if (articleJsonLd.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: safeJsonLd(articleJsonLd.value),
        key: 'jsonld-article',
      })
    }
    scripts.push({
      type: 'application/ld+json',
      innerHTML: safeJsonLd(breadcrumbJsonLd.value),
      key: 'jsonld-breadcrumb',
    })
    return scripts
  }),
})

const { $device } = useNuxtApp()
const selectedTab = ref<'web' | 'mobile'>('web')

const visibleTabs = computed<Array<'web' | 'mobile'>>(() => {
  const tabs: Array<'web' | 'mobile'> = []
  if (props.content?.content?.web) tabs.push('web')
  if (props.content?.content?.mobile) tabs.push('mobile')
  return tabs
})
const showMobileDrawer = ref(false)
const headings = ref<Heading[]>([])
const TITLE_ID = '__article-title__'
const activeHeading = ref<string>('')
const formSubmitted = ref(false)
const showDynamicForm = computed(
  () => !formSubmitted.value && !!props.content?.form?.fields?.length,
)

const extendedFormSchema = computed(() => {
  const originalSchema = props.content?.form

  if (!originalSchema || !originalSchema.fields) return originalSchema

  if (!isInitializing.value && !isLoggedIn.value) {
    const additionalFields = [
      {
        order: -2,
        name: 'name',
        required: true,
        notDisplay: false,
        maxLength: 0,
        minLength: 0,
        text: {
          subtitle: {
            tr: '',
            en: '',
          },
          title: {
            tr: 'Ad Soyad',
            en: 'Name Surname',
          },
          tooltip: {
            tr: '',
            en: '',
          },
          options: {
            tr: [],
            en: [],
          },
        },
        type: 'text',
        id: 'field_name',
      },
      {
        order: -1,
        name: 'email',
        required: true,
        notDisplay: false,
        maxLength: 0,
        minLength: 0,
        text: {
          subtitle: {
            tr: '',
            en: '',
          },
          title: {
            tr: 'E-Posta',
            en: 'Email',
          },
          tooltip: {
            tr: '',
            en: '',
          },
          options: {
            tr: [],
            en: [],
          },
        },
        type: 'email',
        id: 'field_email',
      },
    ]

    return {
      ...originalSchema,
      fields: [...additionalFields, ...originalSchema.fields],
    }
  }

  return originalSchema
})

const checkContentForMobile = () => {
  if (!props.content?.content) return

  if (!props.content.content.web && props.content.content.mobile) {
    selectedTab.value = 'mobile'
    return
  }

  if (props.content.content.mobile && ($device as any)?.isMobile) {
    selectedTab.value = 'mobile'
  }
}

// Single source of truth: derive headings from the rendered DOM (which is
// the same DOM scroll-spy will read via getElementById). Tag IDs while we're
// at it so we never have a parsed-string-vs-DOM race on SPA navigation.
const findActiveArticleBody = (): HTMLElement | null => {
  const all = document.querySelectorAll<HTMLElement>('.content-details__body')
  for (const el of all) {
    if (el.offsetParent !== null) return el
  }
  return all[0] || null
}

const extractHeadings = () => {
  if (typeof window === 'undefined') {
    headings.value = []
    return
  }
  const body = findActiveArticleBody()
  if (!body) {
    headings.value = []
    return
  }

  const h2Elements = body.querySelectorAll('h2')
  const cleaned: Heading[] = []
  h2Elements.forEach((h2, index) => {
    const raw = h2.textContent || ''
    const text = raw.replace(/[\u00A0\u200B-\u200D\uFEFF]/g, '').trim()
    if (!text) return

    if (!h2.id) h2.id = `heading-${index}`
    cleaned.push({ id: h2.id, text })
  })

  headings.value = cleaned
}

let bodyObserver: MutationObserver | null = null

const observeArticleBody = () => {
  if (typeof window === 'undefined') return
  const body = findActiveArticleBody()
  if (!body) return
  if (bodyObserver) bodyObserver.disconnect()
  bodyObserver = new MutationObserver(() => {
    extractHeadings()
    updateActiveHeading()
  })
  bodyObserver.observe(body, { childList: true, subtree: true })
}

const disconnectBodyObserver = () => {
  if (bodyObserver) {
    bodyObserver.disconnect()
    bodyObserver = null
  }
}

// const handleFeedback = async (isHelpful: boolean) => {
//   if (isHelpful && props.content?.id) {
//     await fetchVoteById(props.content.id, selectedTab.value, isHelpful)
//   }
// }

// const handleSubmit = async (data: any) => {
//   if (props.content?.id) {
//     await fetchVoteById(props.content.id, selectedTab.value, false, data.options, data.text)
//   }
// }

const handleDynamicFormSubmit = async (formData: Record<string, any>) => {
  if (props.content?.id) {
    const success = await submitForm(props.content.id, formData, selectedTab.value)

    if (success) {
      formSubmitted.value = true
    }
  }
}

// Ensure canonical URL with root category prefix
const ensureCanonicalUrl = () => {
  const detailSlug = route.params.detail?.toString() || ''
  const rootSlug = props.content?.categories?.[0]?.slug || ''
  if (!detailSlug || !rootSlug) return

  const currentParts = route.path.split('/').filter(Boolean)
  const last = currentParts[currentParts.length - 1]
  const prev = currentParts[currentParts.length - 2]
  if (last === detailSlug && prev !== rootSlug) {
    const target = appLink(`${rootSlug}/${detailSlug}`)
    // Avoid history pollution and loops
    navigateTo(target, { replace: true })
  }
}

const waitForElementById = (
  id: string,
  maxAttempts = 20,
  intervalMs = 50,
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    let attemptsRemaining = maxAttempts
    const tryFind = () => {
      const el = document.getElementById(id)
      if (el) return resolve(el)
      if (attemptsRemaining <= 0) return resolve(null)
      attemptsRemaining -= 1
      setTimeout(tryFind, intervalMs)
    }
    tryFind()
  })
}

const handleMobileHeadingClick = async (headingId: string) => {
  const element = document.getElementById(headingId) || (await waitForElementById(headingId))
  if (!element) return

  const headerEl = document.querySelector('.header') as HTMLElement | null
  const readingBarEl = document.querySelector('.p-reading-progress') as HTMLElement | null
  const headerHeight = headerEl?.offsetHeight ?? 0
  const readingBarHeight = readingBarEl ? 4 : 0

  const targetY =
    window.scrollY + element.getBoundingClientRect().top - (headerHeight + readingBarHeight + 8)

  window.scrollTo({
    top: targetY,
    behavior: 'smooth',
  })

  activeHeading.value = headingId
  showMobileDrawer.value = false
}

const handleMobileTitleClick = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
  activeHeading.value = TITLE_ID
  showMobileDrawer.value = false
}

let rafId: number | null = null

const updateActiveHeading = () => {
  if (headings.value.length === 0 && !props.content?.title) return

  const headerEl = document.querySelector('.header') as HTMLElement | null
  const readingBarEl = document.querySelector('.p-reading-progress') as HTMLElement | null
  const headerHeight = headerEl?.offsetHeight ?? 80
  const readingBarHeight = readingBarEl ? 4 : 0
  const triggerY = headerHeight + readingBarHeight + 16

  let activeId = props.content?.title ? TITLE_ID : headings.value[0]?.id || ''
  for (const heading of headings.value) {
    const el = document.getElementById(heading.id)
    if (!el) continue
    if (el.getBoundingClientRect().top <= triggerY) {
      activeId = heading.id
    } else {
      break
    }
  }

  activeHeading.value = activeId
}

const onScroll = () => {
  if (rafId !== null) return
  rafId = requestAnimationFrame(() => {
    updateActiveHeading()
    rafId = null
  })
}

const startScrollSpy = async () => {
  if (typeof window === 'undefined') return
  await nextTick()
  updateActiveHeading()
  window.addEventListener('scroll', onScroll, { passive: true })
}

const stopScrollSpy = () => {
  if (typeof window === 'undefined') return
  window.removeEventListener('scroll', onScroll)
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

watch(selectedTab, async () => {
  await nextTick()
  // Tab changed — visible body element may have changed; re-attach observer.
  observeArticleBody()
  extractHeadings()
  await nextTick()
  updateActiveHeading()
})

watch(
  () => props.content,
  async () => {
    checkContentForMobile()
    await nextTick()
    observeArticleBody()
    extractHeadings()
    ensureCanonicalUrl()
    await nextTick()
    updateActiveHeading()
  },
)

onMounted(async () => {
  checkContentForMobile()
  await nextTick()
  observeArticleBody()
  extractHeadings()
  ensureCanonicalUrl()
  startScrollSpy()
})

onBeforeUnmount(() => {
  disconnectBodyObserver()
  stopScrollSpy()
})

definePageMeta({
  layout: 'default',
})
</script>

<style lang="scss" scoped>
.content-details__tabs--single :deep(.p26-tabs) {
  display: none;
}
</style>
